import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "../ui/components/Button";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { Table } from "../ui/components/Table";
import { Badge } from "../ui/components/Badge";
import { Avatar } from "../ui/components/Avatar";
import { Loader } from "../ui/components/Loader";
import { Tabs } from "../ui/components/Tabs";
import { useActiveLocations } from "../shared/hooks/useActiveLocations";
import {
  FeatherUpload,
  FeatherCalendar,
  FeatherFileText,
  FeatherDollarSign,
  FeatherCheckCircle,
  FeatherAlertTriangle,
  FeatherRefreshCw,
  FeatherArrowRight,
  FeatherDownload,
  FeatherTrash,
  FeatherZap,
  FeatherUser,
} from "@subframe/core";
import { 
  GSTR2BRecord, 
  ReconciliationStatus, 
  ReconciliationSummary,
  ReconciliationTab,
  ReconciliationCategory,
} from "../types/gstr2bTypes";
import { gstr2bAPI } from "../services/gstr2bApi";
import InvoiceDetailsModal from "../components/InvoiceDetailsModal";
import UploadLogModal from "../components/UploadLogModal";

// Mock data for UI development
const MOCK_RECORDS: GSTR2BRecord[] = [];

const MOCK_SUMMARY: ReconciliationSummary = {
  totalPurchaseEntries: 0,
  total2BInvoices: 0,
  matchedCount: 0,
  suggestiveMatchesCount: 0,
  missingInBooksCount: 0,
  missingIn2BCount: 0,
  mismatchCount: 0,
  excludedCount: 0,
  pendingCount: 0,
  hasNewBillsForRecheck: false,
};

const GSTR2BReconciliationPage: React.FC = () => {
  // Data states
  const [records, setRecords] = useState<GSTR2BRecord[]>([]);
  const [summary, setSummary] = useState<ReconciliationSummary>(MOCK_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [activeTab, setActiveTab] = useState<ReconciliationTab>('matched');
  const [activeCategory, setActiveCategory] = useState<ReconciliationCategory>('all');

  // Upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync state
  const [syncing, setSyncing] = useState(false);

  // Reconciliation state
  const [reconciling, setReconciling] = useState(false);

  const handleRunReconciliation = async () => {
    try {
      setReconciling(true);
      const response = await gstr2bAPI.runReconciliation(filterGstin || undefined);
      if (response.success && response.data) {
        const r = response.data.results;
        const suggestive = (r.suggestive_invoice_mismatch || 0) + (r.suggestive_date_mismatch || 0) + (r.suggestive_amount_mismatch || 0);
        alert(
          `Reconciliation completed!\n\n` +
          `Matched: ${r.matched}\n` +
          `Suggestive matches: ${suggestive}\n` +
          `Missing in books: ${r.missing_in_books}\n` +
          `Total processed: ${response.data.total_processed}`
        );
        await loadReconciliationData();
      } else {
        alert(`Reconciliation failed: ${response.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message || 'Failed to run reconciliation'}`);
    } finally {
      setReconciling(false);
    }
  };

  // Re-check "missing in books" entries against bills uploaded after the 2B sync ran.
  const [recheckingMissing, setRecheckingMissing] = useState(false);

  const handleRecheckMissingInBooks = async () => {
    try {
      setRecheckingMissing(true);
      const response = await gstr2bAPI.reconcileMissingInBooks(filterGstin || undefined);
      if (response.success && response.data) {
        const r = response.data.results;
        const suggestive = (r.suggestive_invoice_mismatch || 0) + (r.suggestive_date_mismatch || 0) + (r.suggestive_amount_mismatch || 0);
        alert(
          `Re-check completed!\n\n` +
          `Newly matched: ${r.matched}\n` +
          `Suggestive matches: ${suggestive}\n` +
          `Still missing in books: ${r.missing_in_books}\n` +
          `Total re-checked: ${response.data.total_processed}`
        );
        await loadReconciliationData();
      } else {
        alert(`Re-check failed: ${response.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message || 'Failed to re-check missing-in-books'}`);
    } finally {
      setRecheckingMissing(false);
    }
  };

  // Download state
  const [downloading, setDownloading] = useState<string | null>(null);
  
  // Convert YYYY-MM to MMYYYY for API (used for sync/process and reconciliation filter)
  const convertFromMonthInput = (yyyyMM: string): string => {
    if (!yyyyMM || !yyyyMM.includes('-')) return '';
    const [year, month] = yyyyMM.split('-');
    return `${month}${year}`;
  };

  // Build reconciliation API params from From/To month filter
  const getReconciliationParams = (): { return_period?: string; from_period?: string; to_period?: string; gstin?: string } => {
    // ZOUR-91: the chosen registration rides along with the period.
    const gstinParam = filterGstin ? { gstin: filterGstin } : {};

    if (filterFromMonth && filterToMonth) {
      return {
        from_period: convertFromMonthInput(filterFromMonth),
        to_period: convertFromMonthInput(filterToMonth),
        ...gstinParam,
      };
    }
    return gstinParam;
  };

  // Format YYYY-MM as "Mon YYYY" for display
  const formatFilterPeriodLabel = (yyyyMM: string): string => {
    if (!yyyyMM || !yyyyMM.includes('-')) return yyyyMM;
    const [y, m] = yyyyMM.split('-');
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  };
  
  // Initialize month inputs with current month (for sync/process operations)
  const getCurrentMonth = (): string => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = String(today.getFullYear());
    return `${year}-${month}`;
  };

  // Returns a YYYY-MM string offset by N months from the current month (negative = past)
  const getMonthOffset = (months: number): string => {
    const today = new Date();
    const d = new Date(today.getFullYear(), today.getMonth() + months, 1);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear());
    return `${year}-${month}`;
  };

  // Selectable bounds for the reconciliation period filter: last 2 years → present
  const filterMinMonth = getMonthOffset(-24);
  const filterMaxMonth = getCurrentMonth();

  const [fromMonthInput, setFromMonthInput] = useState(getCurrentMonth());
  const [toMonthInput, setToMonthInput] = useState(getCurrentMonth());

  // Reconciliation data filter: From / To month (YYYY-MM); empty = no filter (all periods).
  // Defaults to the last 2 years so the initial load is scoped (not an unbounded all-periods load).
  const [filterFromMonth, setFilterFromMonth] = useState<string>(getMonthOffset(-24));
  const [filterToMonth, setFilterToMonth] = useState<string>(getCurrentMonth());

  /**
   * ZOUR-91 — WHICH OF OUR GST REGISTRATIONS this screen is showing.
   *
   * GSTR-2B is generated per GSTIN, so reconciliation is a per-registration
   * operation. The endpoints have always accepted this parameter and the API
   * client has always had `fetchGSTINs()`; the page simply never used either,
   * so every registration's 2B rows were reconciled against one undifferentiated
   * pool of books.
   *
   * '' means every registration — the pre-ZOUR-91 behaviour, and what a client
   * with one GSTIN in the upload log and one registration always gets, because
   * the filter is then hidden entirely.
   */
  const [filterGstin, setFilterGstin] = useState<string>('');
  const [availableGstins, setAvailableGstins] = useState<string[]>([]);
  const { locations: activeLocations, hasMultiple: hasMultipleLocations } = useActiveLocations();

  const showGstinFilter = availableGstins.length > 1 || (hasMultipleLocations && availableGstins.length > 0);

  /** The registration name behind a GSTIN, for a filter nobody can read otherwise. */
  const registrationLabelFor = (gstin: string): string => {
    const match = activeLocations.find(
      (location) => (location.gstin ?? '').replace(/\s+/g, '').toUpperCase() === gstin.replace(/\s+/g, '').toUpperCase(),
    );

    return match ? `${match.name} · ${gstin}` : gstin;
  };

  useEffect(() => {
    void (async () => {
      const response = await gstr2bAPI.fetchGSTINs();
      if (response.success && Array.isArray(response.data)) {
        setAvailableGstins(response.data);
      }
    })();
  }, []);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Reset state
  const [resetting, setResetting] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<GSTR2BRecord | null>(null);
  const [uploadLogModalOpen, setUploadLogModalOpen] = useState(false);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format return period MMYYYY → "Jan 2025"
  const formatReturnPeriod = (period: string): string => {
    if (!period || period.length < 6) return period || '—';
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const m = parseInt(period.substring(0, 2), 10);
    return `${monthNames[m - 1] || period.substring(0, 2)} ${period.substring(2, 6)}`;
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: ReconciliationStatus): "neutral" | "success" | "error" | "warning" => {
    switch (status) {
      case 'matched':
        return 'success';
      case 'suggestive_invoice_mismatch':
      case 'suggestive_date_mismatch':
      case 'suggestive_amount_mismatch':
        return 'warning';
      case 'missing_in_2b':
      case 'missing_in_books':
        return 'error';
      case 'excluded':
      case 'pending':
      default:
        return 'neutral';
    }
  };

  // Get status label
  const getStatusLabel = (status: ReconciliationStatus): string => {
    switch (status) {
      case 'matched':
        return 'Matched';
      case 'suggestive_invoice_mismatch':
        return 'Invoice # Mismatch';
      case 'suggestive_date_mismatch':
        return 'Date Mismatch';
      case 'suggestive_amount_mismatch':
        return 'Amount Mismatch';
      case 'missing_in_2b':
        return 'Missing in 2B';
      case 'missing_in_books':
        return 'Missing in Books';
      case 'excluded':
        return 'Excluded';
      case 'pending':
        return 'Pending';
      default:
        return 'Unknown';
    }
  };

  // Format the reconciled_at datetime ("2026-04-07 11:30:26") to "7 Apr, 11:30 am".
  // Parsed component-wise to avoid timezone shifting of the server-local value.
  const formatMatchedAt = (value: string | null): string => {
    if (!value) return '';
    const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!parts) return value;
    const [, , month, day, hourStr, minute] = parts;
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? 'pm' : 'am';
    hour = hour % 12 || 12;
    return `${parseInt(day, 10)} ${monthNames[parseInt(month, 10) - 1] || month}, ${hour}:${minute} ${ampm}`;
  };

  // Render the auto/manual match badge with the time (and matcher, for manual matches)
  const renderMatchCell = (record: GSTR2BRecord) => {
    if (!record.matchType) {
      return <span className="text-caption font-caption text-subtext-color">—</span>;
    }
    const isManual = record.matchType === 'manual';
    const subline = formatMatchedAt(record.reconciledAt) +
      (isManual && record.matchedByName ? ` · ${record.matchedByName}` : '');
    return (
      <div className="flex flex-col items-start gap-0.5">
        <span
          className={
            isManual
              ? "flex h-6 w-fit items-center gap-1 rounded-md border border-solid border-brand-100 bg-brand-100 px-2 text-caption font-caption text-brand-700"
              : "flex h-6 w-fit items-center gap-1 rounded-md border border-solid border-neutral-100 bg-neutral-100 px-2 text-caption font-caption text-neutral-700"
          }
        >
          {isManual ? <FeatherUser className="text-caption" /> : <FeatherZap className="text-caption" />}
          {isManual ? 'Manual' : 'Auto'}
        </span>
        {subline && (
          <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
            {subline}
          </span>
        )}
      </div>
    );
  };

  // Filter records based on active tab and category
  const filteredRecords = useMemo(() => {
    let filtered = [...records];

    // Filter by tab (Matched/Pending/Excluded/All)
    if (activeTab === 'all') {
      // Show everything
    } else if (activeTab === 'matched') {
      filtered = filtered.filter(r => r.status === 'matched');
    } else if (activeTab === 'excluded') {
      filtered = filtered.filter(r => r.status === 'excluded');
    } else {
      filtered = filtered.filter(r => r.status !== 'matched' && r.status !== 'excluded');
    }

    // Filter by category
    if (activeCategory !== 'all') {
      switch (activeCategory) {
        case 'suggested':
          filtered = filtered.filter(r =>
            r.status === 'suggestive_invoice_mismatch' ||
            r.status === 'suggestive_date_mismatch' ||
            r.status === 'suggestive_amount_mismatch'
          );
          break;
        case 'missing_in_books':
          filtered = filtered.filter(r => r.status === 'missing_in_books');
          break;
        case 'missing_in_2b':
          filtered = filtered.filter(r => r.status === 'missing_in_2b');
          break;
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(r =>
        (r.supplierName && r.supplierName.toLowerCase().includes(q)) ||
        (r.supplierGSTIN && r.supplierGSTIN.toLowerCase().includes(q)) ||
        (r.invoiceNumber && r.invoiceNumber.toLowerCase().includes(q)) ||
        (r.booksInvoiceNumber && r.booksInvoiceNumber.toLowerCase().includes(q))
      );
    }

    return filtered;
  }, [records, activeTab, activeCategory, searchQuery]);

  // Load data on component mount
  useEffect(() => {
    loadReconciliationData();
  }, []);

  // Load reconciliation data (filtered by From/To month when set, else all periods)
  const loadReconciliationData = async (forceAllPeriods?: boolean) => {
    try {
      setLoading(true);
      setError(null);

      const params = forceAllPeriods
        ? (filterGstin ? { gstin: filterGstin } : {})
        : getReconciliationParams();
      const response = await gstr2bAPI.fetchReconciliationData(params);

      if (response.success && response.data) {
        const invoices = response.data.invoices || [];
        const summary = response.data.summary || MOCK_SUMMARY;
        
        // Debug logging
        console.log('📊 Reconciliation Data Loaded:', (params as any)?.from_period ? `Period ${(params as any).from_period}-${(params as any).to_period}` : 'All Periods', {
          totalInvoices: invoices.length,
          summary: summary,
          invoicesByStatus: invoices.reduce((acc: any, inv: GSTR2BRecord) => {
            acc[inv.status] = (acc[inv.status] || 0) + 1;
            return acc;
          }, {})
        });
        
        setRecords(invoices);
        setSummary(summary);
        
        // Update selectedRecord if modal is open - this refreshes the modal with new data
        if (selectedRecord && isModalOpen) {
          const updatedRecord = invoices.find((r: GSTR2BRecord) => r.id === selectedRecord.id);
          if (updatedRecord) {
            setSelectedRecord(updatedRecord);
          }
        }
      } else {
        setError(response.message || 'Failed to load reconciliation data');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading reconciliation data:', err);
      setError('Failed to load reconciliation data');
      setLoading(false);
    }
  };

  // Handle import JSON
  const handleImportJSON = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      alert('Please select a valid JSON file');
      return;
    }

    try {
      setUploading(true);

      const response = await gstr2bAPI.importJSON(file);

      if (response.success && response.data) {
        alert(
          `Successfully imported!\n\n` +
          `Total Invoices: ${response.data.stats.total_invoices}\n` +
          `New Invoices: ${response.data.stats.new_invoices}\n` +
          `Duplicates Skipped: ${response.data.stats.duplicate_invoices}\n` +
          `Total ITC: ₹${response.data.stats.total_itc.toLocaleString('en-IN')}`
        );

        // Reload data
        await loadReconciliationData();
      } else {
        alert(`Upload failed: ${response.message}`);
      }
    } catch (err: any) {
      console.error('Error uploading file:', err);
      alert(`Error: ${err.message || 'Failed to upload file'}`);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle view details
  const handleViewDetails = (record: GSTR2BRecord) => {
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  // Handle sync bills from Zoho (syncs and processes to purchase register in one step)
  const handleSyncZohoBills = async () => {
    if (!fromMonthInput || !toMonthInput) {
      alert('Please select both from and to months');
      return;
    }

    const fromMonth = convertFromMonthInput(fromMonthInput);
    const toMonth = convertFromMonthInput(toMonthInput);

    try {
      // Check if existing data will be affected and warn the user
      const countRes = await gstr2bAPI.getPrCount(fromMonth, toMonth);
      if (countRes.success && countRes.data && countRes.data.count > 0) {
        const confirmed = window.confirm(
          `⚠️ ${countRes.data.count} bill${countRes.data.count === 1 ? '' : 's'} already exist in the purchase register for this period (${countRes.data.date_from} to ${countRes.data.date_to}).\n\n` +
          `Re-syncing will compare each bill with Zoho. If anything has changed, the old record will be archived and replaced with the latest data. Any matched reconciliations will be reset and need re-matching.\n\n` +
          `Continue?`
        );
        if (!confirmed) return;
      }

      setSyncing(true);
      const response = await gstr2bAPI.syncZohoBills(fromMonth, toMonth);

      if (response.success && response.data) {
        const errorsCount = (response.data.errors && Array.isArray(response.data.errors))
          ? response.data.errors.length
          : 0;

        const unreconciled = response.data.pr_unreconciled || 0;
        const details: Array<{bill_id: string; vendor_name: string; invoice_no: string; old_amount: number; new_amount: number}> =
          response.data.pr_unreconcile_details || [];

        let unreconciledLines = '';
        if (unreconciled > 0) {
          unreconciledLines = `\n⚠️ Auto-unmatched (data changed in Zoho): ${unreconciled}\n`;
          details.slice(0, 5).forEach((d) => {
            unreconciledLines += `  • ${d.vendor_name} | ${d.invoice_no} | ₹${d.old_amount.toFixed(2)} → ₹${d.new_amount.toFixed(2)}\n`;
          });
          if (details.length > 5) {
            unreconciledLines += `  ...and ${details.length - 5} more\n`;
          }
          unreconciledLines += `Please re-match these in the reconciliation view.\n`;
        }

        alert(
          `Sync completed!\n\n` +
          `New Bills: ${response.data.bills_synced}\n` +
          `Re-synced (existing): ${response.data.bills_updated_in_staging || 0}\n` +
          `Bills Processed: ${response.data.bills_processed}\n` +
          `Skipped (no GSTIN): ${response.data.bills_skipped_no_gstin || 0}\n` +
          `Purchase Register Created: ${response.data.purchase_register_entries_created}\n` +
          `Purchase Register Replaced (changed): ${response.data.pr_replaced || 0}\n` +
          unreconciledLines +
          (errorsCount > 0 ? `Errors: ${errorsCount}\n` : '')
        );

        // Refresh reconciliation data
        await loadReconciliationData();
      } else {
        alert(`Sync failed: ${response.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      console.error('Error syncing bills:', err);
      alert(`Error: ${err.message || 'Failed to sync bills'}`);
    } finally {
      setSyncing(false);
    }
  };

  // Handle download Excel for category (uses current From/To filter when set)
  const handleDownloadExcel = async (category: string) => {
    try {
      setDownloading(category);
      const p = getReconciliationParams();
      await gstr2bAPI.downloadExcel(
        category,
        p.return_period,
        p.from_period,
        p.to_period
      );
    } catch (err: any) {
      console.error('Error downloading Excel:', err);
      alert(`Error: ${err.message || 'Failed to download Excel'}`);
    } finally {
      setDownloading(null);
    }
  };

  const handleReset = async () => {
    const confirmed = window.confirm(
      'This will delete ALL imported 2B data, upload history, and reset all reconciliation matches.\n\n' +
      'Your Zoho bills and purchase register data will NOT be affected.\n\n' +
      'You will need to re-import all 2B JSON files after this.\n\n' +
      'Are you sure?'
    );
    if (!confirmed) return;

    try {
      setResetting(true);
      const response = await gstr2bAPI.resetAll();
      if (response.success && response.data) {
        alert(
          `Reset completed!\n\n` +
          `Invoices deleted: ${response.data.invoices_deleted}\n` +
          `Uploads deleted: ${response.data.uploads_deleted}\n\n` +
          `You can now re-import your 2B files.`
        );
        await loadReconciliationData();
      } else {
        alert(`Reset failed: ${response.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message || 'Failed to reset'}`);
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        record={selectedRecord}
        onVerificationSuccess={loadReconciliationData}
      />

      {/* Upload Log Modal */}
      <UploadLogModal
        isOpen={uploadLogModalOpen}
        onClose={() => setUploadLogModalOpen(false)}
      />

      <div className="flex h-full w-full flex-col items-start overflow-auto">
        {/* Header Section */}
        <div className="flex w-full flex-col items-start gap-6 border-b border-solid border-neutral-border bg-default-background px-8 py-6 flex-shrink-0">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                GSTR-2B Reconciliation
              </span>
              <span className="text-body font-body text-subtext-color">
                {filterFromMonth && filterToMonth
                  ? `Showing data for ${formatFilterPeriodLabel(filterFromMonth)} – ${formatFilterPeriodLabel(filterToMonth)}`
                  : 'Reconcile your purchase invoices with GSTR-2B data (All Periods)'}
              </span>
            </div>

            {/* Period Filter (last 2 years → present) */}
            <div className="flex flex-wrap items-center gap-3">
              {/* ZOUR-91 — the registration this screen is reconciling.
                  Hidden when there is nothing to choose between: one GSTIN in
                  the upload log and one registration. */}
              {showGstinFilter && (
                <>
                  <label className="text-body font-body text-subtext-color whitespace-nowrap">
                    Registration:
                  </label>
                  <select
                    value={filterGstin}
                    onChange={(e) => setFilterGstin(e.target.value)}
                    disabled={loading}
                    className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                    aria-label="GST registration"
                  >
                    <option value="">All registrations</option>
                    {availableGstins.map((gstin) => (
                      <option key={gstin} value={gstin}>
                        {registrationLabelFor(gstin)}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <label className="text-body font-body text-subtext-color whitespace-nowrap">
                Filter Period:
              </label>
              <input
                type="month"
                value={filterFromMonth}
                min={filterMinMonth}
                max={filterMaxMonth}
                onChange={(e) => setFilterFromMonth(e.target.value)}
                disabled={loading}
                className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                style={{ width: '160px' }}
              />
              <FeatherArrowRight className="text-subtext-color w-4 h-4" />
              <input
                type="month"
                value={filterToMonth}
                min={filterMinMonth}
                max={filterMaxMonth}
                onChange={(e) => setFilterToMonth(e.target.value)}
                disabled={loading}
                className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                style={{ width: '160px' }}
              />
              <Button
                variant="brand-primary"
                onClick={() => loadReconciliationData()}
                disabled={loading || !filterFromMonth || !filterToMonth}
                loading={loading}
              >
                Apply
              </Button>
              <Button
                variant="neutral-secondary"
                onClick={() => {
                  setFilterFromMonth('');
                  setFilterToMonth('');
                  loadReconciliationData(true);
                }}
                disabled={loading}
              >
                All Periods
              </Button>
            </div>
          </div>

          {/* Setup Steps Card */}
          <div className="flex w-full flex-row gap-6 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
            {/* Step 1 - Zoho Sync */}
            <div className="flex flex-1 flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-600">
                  <span className="text-body-bold font-body-bold">1</span>
                </div>
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Zoho Sync
                </span>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="text-body font-body text-subtext-color whitespace-nowrap">
                    Sync Month Range:
                  </label>
                  <input
                    type="month"
                    value={fromMonthInput}
                    onChange={(e) => setFromMonthInput(e.target.value)}
                    disabled={syncing}
                    className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                    style={{ width: '180px' }}
                  />
                  <FeatherArrowRight className="text-subtext-color w-4 h-4" />
                  <input
                    type="month"
                    value={toMonthInput}
                    onChange={(e) => setToMonthInput(e.target.value)}
                    disabled={syncing}
                    className="px-3 py-2 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                    style={{ width: '180px' }}
                  />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    variant="brand-primary"
                    icon={<FeatherRefreshCw />}
                    onClick={handleSyncZohoBills}
                    disabled={syncing || !fromMonthInput || !toMonthInput}
                    loading={syncing}
                  >
                    {syncing ? 'Syncing...' : 'Sync Bills from Zoho'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Vertical Divider */}
            <div className="border-l border-solid border-neutral-border"></div>

            {/* Step 2 - Import 2B */}
            <div className="flex flex-1 flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-600">
                  <span className="text-body-bold font-body-bold">2</span>
                </div>
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Import 2B
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="neutral-secondary"
                  icon={<FeatherUpload />}
                  onClick={handleImportJSON}
                  disabled={uploading}
                  loading={uploading}
                  className="self-start"
                >
                  {uploading ? 'Uploading...' : 'Import 2B JSON'}
                </Button>
                <Button
                  variant="neutral-tertiary"
                  icon={<FeatherFileText />}
                  onClick={() => setUploadLogModalOpen(true)}
                  className="self-start"
                >
                  Upload Log
                </Button>
                <Button
                  variant="destructive-secondary"
                  size="medium"
                  icon={<FeatherTrash />}
                  onClick={handleReset}
                  disabled={resetting}
                  loading={resetting}
                >
                  {resetting ? 'Resetting...' : 'Reset & Re-import'}
                </Button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="w-full border-t border-solid border-neutral-border"></div>

          {/* Summary Statistics Cards */}
          <div className="flex w-full flex-wrap items-start gap-4">
            {/* Total Purchase Register Entries */}
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border px-6 py-6">
              <div className="flex items-center gap-2">
                <IconWithBackground
                  variant="neutral"
                  size="small"
                  icon={<FeatherFileText />}
                />
                <span className="text-body-bold font-body-bold text-subtext-color">
                  Total Purchase Register Entries
                </span>
              </div>
              <span className="text-heading-2 font-heading-2 text-default-font">
                {loading ? <Loader size="small" /> : (summary?.totalPurchaseEntries || 0).toLocaleString('en-IN')}
              </span>
            </div>

            {/* Total 2B (Books) Invoices */}
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border px-6 py-6">
              <div className="flex items-center gap-2">
                <IconWithBackground
                  variant="brand"
                  size="small"
                  icon={<FeatherDollarSign />}
                />
                <span className="text-body-bold font-body-bold text-subtext-color">
                  Total 2B (Books) Invoices
                </span>
              </div>
              <span className="text-heading-2 font-heading-2 text-brand-600">
                {loading ? <Loader size="small" /> : (summary?.total2BInvoices || 0).toLocaleString('en-IN')}
              </span>
            </div>

            {/* Matched */}
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border px-6 py-6">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <IconWithBackground
                    variant="success"
                    size="small"
                    icon={<FeatherCheckCircle />}
                  />
                  <span className="text-body-bold font-body-bold text-subtext-color">
                    Matched
                  </span>
                </div>
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  icon={<FeatherDownload />}
                  onClick={() => handleDownloadExcel('matched')}
                  disabled={downloading !== null || (summary?.matchedCount || 0) === 0}
                  loading={downloading === 'matched'}
                />
              </div>
              <span className="text-heading-2 font-heading-2 text-success-600">
                {loading ? <Loader size="small" /> : (summary?.matchedCount || 0).toLocaleString('en-IN')}
              </span>
            </div>

            {/* Suggestive Matches */}
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border px-6 py-6">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <IconWithBackground
                    variant="warning"
                    size="small"
                    icon={<FeatherAlertTriangle />}
                  />
                  <span className="text-body-bold font-body-bold text-subtext-color">
                    Suggestive Matches
                  </span>
                </div>
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  icon={<FeatherDownload />}
                  onClick={() => handleDownloadExcel('suggested')}
                  disabled={downloading !== null || (summary?.suggestiveMatchesCount || 0) === 0}
                  loading={downloading === 'suggested'}
                />
              </div>
              <span className="text-heading-2 font-heading-2 text-warning-600">
                {loading ? <Loader size="small" /> : (summary?.suggestiveMatchesCount || 0).toLocaleString('en-IN')}
              </span>
            </div>
          </div>
        </div>

        {/* Pending reconciliation banner */}
        {(summary?.pendingCount || 0) > 0 && (
          <div className="flex w-full items-center justify-between gap-4 bg-warning-50 border-b border-solid border-warning-200 px-8 py-3">
            <div className="flex items-center gap-2">
              <FeatherAlertTriangle className="text-warning-600" />
              <span className="text-body font-body text-warning-700">
                {summary!.pendingCount} GSTR-2B invoice{summary!.pendingCount === 1 ? '' : 's'} pending reconciliation — run reconciliation to match them against your purchase register.
              </span>
            </div>
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherRefreshCw />}
              onClick={handleRunReconciliation}
              loading={reconciling}
              disabled={reconciling}
            >
              Run Reconciliation
            </Button>
          </div>
        )}

        {/* Missing-in-books re-check banner: only when new bills arrived since the last re-check */}
        {(summary?.missingInBooksCount || 0) > 0 && summary?.hasNewBillsForRecheck && (
          <div className="flex w-full items-center justify-between gap-4 bg-error-50 border-b border-solid border-error-200 px-8 py-3">
            <div className="flex items-center gap-2">
              <FeatherAlertTriangle className="text-error-600" />
              <span className="text-body font-body text-error-700">
                {summary!.missingInBooksCount} GSTR-2B invoice{summary!.missingInBooksCount === 1 ? '' : 's'} not found in books — re-check to match against bills uploaded after the last sync.
              </span>
            </div>
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherRefreshCw />}
              onClick={handleRecheckMissingInBooks}
              loading={recheckingMissing}
              disabled={recheckingMissing}
            >
              Re-check against Books
            </Button>
          </div>
        )}

        {/* Tabs and Category Filters Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 pt-4 flex-shrink-0">
          {/* Tabs */}
          <Tabs>
            <Tabs.Item 
              active={activeTab === 'matched'} 
              onClick={() => {
                setActiveTab('matched');
                setActiveCategory('all');
                setSearchQuery('');
              }}
            >
              Matched
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === 'pending'}
              onClick={() => {
                setActiveTab('pending');
                setActiveCategory('all');
                setSearchQuery('');
              }}
            >
              Pending
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === 'excluded'}
              onClick={() => {
                setActiveTab('excluded');
                setActiveCategory('all');
                setSearchQuery('');
              }}
            >
              Excluded
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === 'all'}
              onClick={() => {
                setActiveTab('all');
                setActiveCategory('all');
                setSearchQuery('');
              }}
            >
              All
            </Tabs.Item>
          </Tabs>

          {/* Category Filter Buttons - Only show for Pending tab */}
          {activeTab === 'pending' && (
            <div className="flex w-full flex-wrap items-center gap-3 pb-4">
              <Button
                variant={activeCategory === 'suggested' ? 'brand-primary' : 'neutral-secondary'}
                size="medium"
                onClick={() => setActiveCategory('suggested')}
              >
                Suggested Matches ({summary?.suggestiveMatchesCount || 0})
              </Button>
              <Button
                variant={activeCategory === 'missing_in_books' ? 'brand-primary' : 'neutral-secondary'}
                size="medium"
                onClick={() => setActiveCategory('missing_in_books')}
              >
                2B entries not In books ({summary?.missingInBooksCount || 0})
              </Button>
              <Button
                variant={activeCategory === 'missing_in_2b' ? 'brand-primary' : 'neutral-secondary'}
                size="medium"
                onClick={() => setActiveCategory('missing_in_2b')}
              >
                Book entries not in 2B ({summary?.missingIn2BCount || 0})
              </Button>
              <Button
                variant={activeCategory === 'all' ? 'brand-primary' : 'neutral-secondary'}
                size="medium"
                onClick={() => setActiveCategory('all')}
              >
                All Pending
              </Button>
            </div>
          )}
        </div>

        {/* Table Section */}
        <div className="flex w-full flex-col items-start bg-default-background px-8 py-6 flex-shrink-0">
          {/* Table Title with Search and Download */}
          <div className="flex w-full items-center justify-between mb-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {activeTab === 'all' ? 'All Entries' :
                activeTab === 'matched' ? 'Matched Entries' :
                activeTab === 'excluded' ? 'Excluded Entries' :
                activeCategory === 'suggested' ? 'Suggested Matches' :
                activeCategory === 'missing_in_books' ? '2B Entries Not In Books' :
                activeCategory === 'missing_in_2b' ? 'Book Entries Not in 2B' :
                'Pending Entries'}
            </span>
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search by supplier, GSTIN, invoice#..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="px-3 py-1.5 border border-solid border-neutral-border rounded-md text-body font-body bg-default-background"
                style={{ width: '280px' }}
              />
              <span className="text-body font-body text-subtext-color whitespace-nowrap">
                {filteredRecords.length} {filteredRecords.length === 1 ? 'entry' : 'entries'}
              </span>
              <Button
                variant="neutral-secondary"
                size="small"
                icon={<FeatherDownload />}
                onClick={() => {
                  const category = activeTab === 'matched' 
                    ? 'matched' 
                    : activeCategory === 'suggested' 
                    ? 'suggested'
                    : activeCategory === 'missing_in_books'
                    ? 'missing_in_books'
                    : activeCategory === 'missing_in_2b'
                    ? 'missing_in_2b'
                    : 'all_pending';
                  handleDownloadExcel(category);
                }}
                disabled={downloading !== null || filteredRecords.length === 0}
                loading={downloading !== null}
              >
                {downloading ? 'Downloading...' : 'Download Excel'}
              </Button>
            </div>
          </div>

          <div className="w-full overflow-x-auto">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Invoice Details</Table.HeaderCell>
                <Table.HeaderCell>Supplier</Table.HeaderCell>
                {activeTab === 'all' && <Table.HeaderCell>Invoice Date</Table.HeaderCell>}
                {activeTab === 'all' && <Table.HeaderCell>2B Period</Table.HeaderCell>}
                {activeTab === 'matched' && <Table.HeaderCell>2B Period</Table.HeaderCell>}
                <Table.HeaderCell>Books Entry</Table.HeaderCell>
                <Table.HeaderCell>2B Entry</Table.HeaderCell>
                {activeTab === 'matched' && <Table.HeaderCell>Match</Table.HeaderCell>}
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.HeaderRow>
            }
          >
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={activeTab === 'all' ? 8 : activeTab === 'matched' ? 8 : 6}>
                  <div className="flex w-full items-center justify-center py-8">
                    <Loader />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : error ? (
              <Table.Row>
                <Table.Cell colSpan={activeTab === 'all' ? 8 : activeTab === 'matched' ? 8 : 6}>
                  <div className="flex w-full items-center justify-center py-8">
                    <span className="text-body font-body text-error-600">
                      {error}
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : filteredRecords.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={activeTab === 'all' ? 8 : activeTab === 'matched' ? 8 : 6}>
                  <div className="flex w-full items-center justify-center py-8">
                    <span className="text-body font-body text-neutral-500">
                      No entries found for the selected filters
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              filteredRecords.map((record) => (
                <Table.Row key={record.id}>
                  <Table.Cell>
                    <div className="flex flex-col items-start gap-0.5">
                      <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                        {record.invoiceNumber}
                      </span>
                      <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
                        {record.invoiceDate}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-start gap-2">
                      <Avatar size="small" square={false} className="flex-shrink-0">
                        {(record.supplierName || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <span
                        className="block max-w-[180px] break-words line-clamp-3 text-body font-body text-default-font"
                        title={record.supplierName || 'Unknown'}
                      >
                        {record.supplierName || 'Unknown'}
                      </span>
                    </div>
                  </Table.Cell>
                  {activeTab === 'all' && (
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {record.invoiceDate || '—'}
                      </span>
                    </Table.Cell>
                  )}
                  {activeTab === 'all' && (
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {formatReturnPeriod(record.returnPeriod)}
                      </span>
                    </Table.Cell>
                  )}
                  {activeTab === 'matched' && (
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {formatReturnPeriod(record.returnPeriod)}
                      </span>
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {formatCurrency(record.booksAmount)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {formatCurrency(record.gstr2bAmount)}
                    </span>
                  </Table.Cell>
                  {activeTab === 'matched' && (
                    <Table.Cell>
                      {renderMatchCell(record)}
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <Badge variant={getStatusBadgeVariant(record.status)}>
                      {getStatusLabel(record.status)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={() => handleViewDetails(record)}
                    >
                      View
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table>
          </div>
        </div>
      </div>
    </>
  );
};

export default GSTR2BReconciliationPage;

